/**
 * Product Card Web Component
 *
 * @description
 * Handles product card interactions including color swatch changes and image slider.
 * Uses Section Rendering API to fetch updated slider content on color change.
 */
import Swiper from "https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.mjs";

(() => {
  if (!customElements.get("custom-product-card")) {
    class CustomProductCard extends HTMLElement {
      // Cache for fetched slider content
      static sliderCache = new Map();

      connectedCallback() {
        this.init();
      }

      setVariables() {
        this.variantDataEl = this.querySelector("[data-variant-data]");
        this.showColorSwatches = this.dataset.showColorSwatches === "true";
        this.productHandle = this.dataset.productHandle;
        this.aspectRatio = this.dataset.aspectRatio || "1-1";
        this.showNavigation = this.dataset.showNavigation || "true";
        this.colorDisplayMode = this.dataset.colorDisplayMode || "swatches";

        this.colorWrapper = this.querySelector(".card-product__color-wrapper");
        this.colorNames = this.querySelector("[data-color-names]");
        this.beforeEl = this.colorWrapper?.querySelector(".before");
        this.afterEl = this.colorWrapper?.querySelector(".after");

        if (this.variantDataEl) {
          this.variantData = JSON.parse(this.variantDataEl.textContent);
        }

        this.imageEl = this.querySelector("[data-product-image]");
        this.linkEls = this.querySelectorAll("[data-product-link]");
        this.priceEl = this.querySelector("[data-product-price]");
        this.comparePriceEl = this.querySelector("[data-compare-price]");
        this.sliderContainer = this.querySelector(".card-product__media-wrapper");
        this.sliderEl = this.querySelector("product-card-slider");
        this.hasSlider = !!this.sliderEl;

        this.colorClickHandler = this.handleColorClick.bind(this);
        this.isLoading = false;
      }

      setEvents() {
        if (this.showColorSwatches && this.variantDataEl) {
          this.colorNames?.addEventListener("click", this.colorClickHandler);
        }
        if (this.colorNames && this.colorWrapper) {
          this.colorNames.addEventListener("scroll", this.colorScrollHandler.bind(this));
          this.colorNames.addEventListener("wheel", (e) => e.stopPropagation(), { passive: true });
          this.colorNames.addEventListener("touchmove", (e) => e.stopPropagation(), { passive: true });
        }
      }

      init() {
        this.setVariables();
        this.setEvents();

        if(this.colorNames && this.colorWrapper){
          this.checkScrollAndBlurEndOfList();
        }

        if (this.hasSlider && this.colorDisplayMode !== "count" && this.variantData) {
          this.cacheInitialSlider();
        }
      }

      cacheInitialSlider() {
        const firstColorEl = this.colorNames?.firstElementChild;
        if (!firstColorEl) return;

        const variantId = parseInt(firstColorEl.dataset.variantId);
        if (!variantId) return;

        const cacheKey = `${this.productHandle}-${variantId}-${this.colorDisplayMode}`;

        if (!CustomProductCard.sliderCache.has(cacheKey) && this.sliderEl) {
          const sliderHtml = this.sliderEl.outerHTML;
          CustomProductCard.sliderCache.set(cacheKey, sliderHtml);
        }
      }

      checkScrollAndBlurEndOfList() {
        if (!this.colorNames || !this.afterEl) return;
        const scrollPosition = this.colorNames.scrollLeft + this.colorNames.clientWidth;
        this.afterEl.classList.toggle("active", scrollPosition !== this.colorNames.scrollWidth);
      }

      colorScrollHandler(event) {
        const target = event.currentTarget;
        const scrollPosition = target.scrollLeft + target.clientWidth;

        this.beforeEl?.classList.toggle("active", target.scrollLeft !== 0);
        this.afterEl?.classList.toggle("active", scrollPosition !== target.scrollWidth);
      }

      async handleColorClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const colorNameEl = event.target.closest("[data-color-name]");
        if (!colorNameEl || this.isLoading) return;

        const variantId = parseInt(colorNameEl.dataset.variantId);
        const variant = this.variantData.variants.find((v) => v.id === variantId);
        if (!variant) return;

        // Prevent action if clicking already selected color
        const isAlreadySelected = colorNameEl.classList.contains("active");

        if (isAlreadySelected) {
          return;
        }

        this.colorNames.querySelectorAll("[data-color-name]").forEach((el) => {
          el.classList.remove("active");
        });
        colorNameEl.classList.add("active");


        // Update slider via Section Rendering API
        if (this.hasSlider) {
          await this.fetchAndUpdateSlider(variantId);
        } else {
          this.updateImage(variant);
        }

        this.updatePrice(variant);
        this.updateLinks(variant);
        this.updateBadges(variant);
      }

      async fetchAndUpdateSlider(variantId) {
        const cacheKey = `${this.productHandle}-${variantId}-${this.colorDisplayMode}`;

        // Check cache first
        if (CustomProductCard.sliderCache.has(cacheKey)) {
          this.replaceSlider(CustomProductCard.sliderCache.get(cacheKey));
          return;
        }

        this.isLoading = true;
        this.sliderEl?.classList.add("loading");
        this.sliderContainer?.classList.add("loading");

        try {
          const url = `${window.Shopify.routes.root}products/${this.productHandle}?variant=${variantId}&aspect_ratio=${this.aspectRatio}&show_navigation=${this.showNavigation}&color_display_mode=${this.colorDisplayMode}&section_id=card-product-slider`;
          const response = await fetch(url);

          if (!response.ok) throw new Error("Failed to fetch slider");

          const html = await response.text();

          // Cache the response
          CustomProductCard.sliderCache.set(cacheKey, html);

          this.replaceSlider(html);
        } catch (error) {
          console.error("Error fetching slider:", error);
        } finally {
          this.isLoading = false;
          this.sliderContainer?.classList.remove("loading");
        }
      }

      replaceSlider(html) {
        // Destroy old swiper
        if (this.sliderEl?.swiper) {
          this.sliderEl.destroy();
        }

        const topContent = this.sliderContainer.querySelector(".card-product__top-content");

        // Create temporary container to parse HTML
        const temp = document.createElement("div");
        temp.innerHTML = html;
        const newSlider = temp.querySelector("product-card-slider");

        if (newSlider) {
          this.sliderEl?.remove();

          if (topContent) {
            topContent.after(newSlider);
          } else {
            this.sliderContainer.prepend(newSlider);
          }

          this.sliderEl = newSlider;
          this.sliderEl.init();
          this.sliderEl.classList.remove("loading");

          // Update links reference
          this.linkEls = this.querySelectorAll("[data-product-link]");
        }
      }

      updateImage(variant) {
        if (!this.imageEl) return;

        const newImageSrc = variant.featured_image || this.variantData.featured_image;
        if (!newImageSrc) return;

        this.imageEl.src = newImageSrc;
        this.imageEl.srcset = this.buildSrcset(newImageSrc);
      }

      buildSrcset = (src) => {
        const widths = [300, 400, 500, 600, 700, 800];
        return widths
          .map((w) => {
            let url;
            if (/width=\d+/.test(src)) {
              url = src.replace(/width=\d+/, `width=${w}`);
            } else if (src.includes("?")) {
              url = `${src}&width=${w}`;
            } else {
              url = `${src}?width=${w}`;
            }
            return `${url} ${w}w`;
          })
          .join(", ");
      }

      updatePrice(variant) {
        if (!this.priceEl) return;

        const formattedPrice = this.formatMoney(variant.price);
        this.priceEl.textContent = formattedPrice;

        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.priceEl.classList.add("card-product__price--sale");
          if (this.comparePriceEl) {
            this.comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
          }
        } else {
          this.priceEl.classList.remove("card-product__price--sale");
          if (this.comparePriceEl) {
            this.comparePriceEl.textContent = "";
          }
        }
      }

      formatMoney(cents) {
        const amount = (cents / 100).toLocaleString('ja-JP');
        return `${window.theme?.currentCurrency?.symbol || "$"}${amount}`;
      }

      updateLinks(variant) {
        const newUrl = variant.variant_url;
        this.linkEls = this.querySelectorAll("[data-product-link]");
        this.linkEls.forEach((link) => {
          link.href = newUrl;
        });
      }

      updateBadges(variant) {
        let variantId = variant.id.toString();
        this.querySelectorAll(".card-product__badge[data-variants]").forEach(el => {
          let variantIds = el.dataset.variants.split(",");
          if (variantIds.indexOf(variantId) >= 0) {
            el.style.display = "";
          } else {
            el.style.display = "none";
          }
        });
      }
    }

    customElements.define("custom-product-card", CustomProductCard);
  }

  if (!customElements.get("product-card-slider")) {
    class ProductCardSlider extends HTMLElement {
      constructor() {
        super();
        this.swiper = null;
      }

      connectedCallback() {
        this.init()

      }

      disconnectedCallback() {
        this.destroy();
      }

      destroy() {
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }
      }

      init() {
        const swiperContainer = this.querySelector(".swiper");
        const showNavigation = this.getAttribute("data-show-navigation") === "true";
        const prevButton = this.querySelector(".card-product__slider-nav--prev");
        const nextButton = this.querySelector(".card-product__slider-nav--next");

        if (!swiperContainer) return;

        this.swiper = new Swiper(swiperContainer, {
          slidesPerView: 1,
          spaceBetween: 0,
          loop: false,
          speed: 300,
          grabCursor: true,
          nested: true,
          navigation: showNavigation ? {
            prevEl: prevButton,
            nextEl: nextButton,
          } : false
        });
      }
    }

    customElements.define("product-card-slider", ProductCardSlider);
  }
})();
